import React, { useState, useEffect } from 'react';
import DateCalendar from './DateCalendar';
import SuccessMessage from './SuccessMessage';
import ErrorMessage from './ErrorMessage';
import CourseSelector from './form-components/CourseSelector';
import LoginModal from './LoginModal';
import { useSession } from '../lib/auth';
import { apiService, MonitoringRequestData, GolfCourse } from '../services/api';
import { 
  Calendar, 
  Clock, 
  Phone, 
  Mail, 
  Play,
  Shield
} from 'lucide-react';

interface MonitoringFormData {
  courseIds: number[];
  preferredDates: string[];
  startTime: string;
  endTime: string;
  numberOfPlayers: number;
  contactPhone: string;
  contactEmail: string;
  notificationMethod: 'sms' | 'email' | 'both';
}

interface FormErrors {
  courseIds?: string;
  preferredDates?: string;
  timeRange?: string;
  contactPhone?: string;
  contactEmail?: string;
}

const MonitoringForm: React.FC = () => {
  const { data: session } = useSession();
  const [formData, setFormData] = useState<MonitoringFormData>({
    courseIds: [],
    preferredDates: [],
    startTime: '06:00',
    endTime: '18:00',
    numberOfPlayers: 0,
    contactPhone: '',
    contactEmail: '',
    notificationMethod: 'email'
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [golfCourses, setGolfCourses] = useState<GolfCourse[]>([]);
  const [isLoadingCourses, setIsLoadingCourses] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState({ title: '', message: '', errors: [] as string[] });

  // 获取球场数据
  useEffect(() => {
    const fetchGolfCourses = async () => {
      try {
        setIsLoadingCourses(true);
        const courses = await apiService.getGolfCourses();
        setGolfCourses(courses);
      } catch (error) {
        console.error('Failed to load golf courses:', error);
        setGolfCourses([
          { id: 1, name: 'Langara Golf Course', location: 'Vancouver, BC', is_active: true, last_updated: new Date().toISOString(), api_source: 'vancouver' },
          { id: 2, name: 'Fraserview Golf Course', location: 'Vancouver, BC', is_active: true, last_updated: new Date().toISOString(), api_source: 'vancouver' },
          { id: 3, name: 'McCleery Golf Course', location: 'Vancouver, BC', is_active: true, last_updated: new Date().toISOString(), api_source: 'vancouver' },
          { id: 4, name: 'Burnaby Mountain Golf Course', location: 'Burnaby, BC', is_active: true, last_updated: new Date().toISOString(), api_source: 'burnaby' },
          { id: 5, name: 'Riverway Golf Course', location: 'Burnaby, BC', is_active: true, last_updated: new Date().toISOString(), api_source: 'burnaby' }
        ]);
      } finally {
        setIsLoadingCourses(false);
      }
    };

    fetchGolfCourses();
  }, []);

  // 当用户登录后，自动填充邮箱地址
  useEffect(() => {
    if (session?.user?.email && !formData.contactEmail) {
      setFormData(prev => ({
        ...prev,
        contactEmail: session.user.email
      }));
    }
  }, [session?.user?.email, formData.contactEmail]);

  const handleCourseChange = (courseId: number, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      courseIds: checked 
        ? [...prev.courseIds, courseId]
        : prev.courseIds.filter(id => id !== courseId)
    }));
  };

  const handleDateChange = (date: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      preferredDates: checked
        ? [...prev.preferredDates, date]
        : prev.preferredDates.filter(d => d !== date)
    }));
  };


  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // 验证球场选择
    if (formData.courseIds.length === 0) {
      newErrors.courseIds = 'Please select at least one golf course';
    }

    // 验证日期选择
    if (formData.preferredDates.length === 0) {
      newErrors.preferredDates = 'Please select at least one preferred date';
    }

    // 验证时间范围
    const startHour = parseInt(formData.startTime.split(':')[0]);
    const endHour = parseInt(formData.endTime.split(':')[0]);
    if (endHour <= startHour) {
      newErrors.timeRange = 'End time must be after start time';
    }

    // 验证邮箱
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.contactEmail) {
      newErrors.contactEmail = 'Email address is required';
    } else if (!emailRegex.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid email address';
    }

    // Phone number is optional (SMS notifications not supported in current version)
    // Validation logic simplified, only email verification required

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 检查用户是否已登录
    if (!session?.user) {
      setShowLoginModal(true);
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Prepare API request data
      const requestData: MonitoringRequestData = {
        courseIds: formData.courseIds,
        preferredDates: formData.preferredDates,
        startTime: formData.startTime,
        endTime: formData.endTime,
        numberOfPlayers: formData.numberOfPlayers,
        contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail,
        notificationMethod: formData.notificationMethod
      };

      console.log('Submitting monitoring request:', requestData);
      
      // Send to backend API
      const response = await apiService.createMonitoringRequest(requestData);
      
      console.log('Monitoring request created:', response);
      
      // Check if any courses were removed due to booking window restrictions
      if (response.removedCourses && response.removedCourses.length > 0) {
        const removedCourseNames = response.removedCourses.map((course: any) => 
          `• ${course.name} (${course.reason})`
        ).join('\n');
        
        setErrorDetails({
          title: 'Some Courses Removed',
          message: `${response.warningMessage}\n\n${removedCourseNames}\n\nMonitoring has been set up for the remaining valid courses.`,
          errors: []
        });
        setShowErrorMessage(true);
      } else {
        // Show success message only if no courses were removed
        setShowSuccessMessage(true);
      }
      
      // Reset form
      setFormData({
        courseIds: [],
        preferredDates: [],
        startTime: '06:00',
        endTime: '18:00',
        numberOfPlayers: 0,
        contactPhone: '',
        contactEmail: '',
        notificationMethod: 'email'
      });
      setErrors({});
      
    } catch (error: any) {
      console.error('Submit failed:', error);
      
      // Handle API validation errors
      if (error.response?.data?.data?.validationErrors) {
        const validationErrors = error.response.data.data.validationErrors;
        const newErrors: FormErrors = {};
        const errorList: string[] = [];
        
        validationErrors.forEach((err: any) => {
          newErrors[err.field as keyof FormErrors] = err.message;
          errorList.push(err.message);
        });
        
        setErrors(newErrors);
        setErrorDetails({
          title: 'Form Validation Failed',
          message: 'Please check and correct the following errors:',
          errors: errorList
        });
        setShowErrorMessage(true);
      } else {
        setErrorDetails({
          title: 'Submission Failed',
          message: error.response?.data?.error || error.message || 'Network error, please try again later.',
          errors: []
        });
        setShowErrorMessage(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <>
      {/* User Status Indicator */}
      {session?.user && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <Shield className="text-green-600" size={20} />
            <div>
              <p className="text-sm font-medium text-green-800">
                Signed in as {session.user.name || session.user.email}
              </p>
              <p className="text-xs text-green-600">
                Your waitlist requests will be saved to your account
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Golf Course Selection */}
        <CourseSelector
          golfCourses={golfCourses}
          selectedCourseIds={formData.courseIds}
          onCourseChange={handleCourseChange}
          isLoading={isLoadingCourses}
          error={errors.courseIds}
        />

        {/* Date Selection */}
        <div className="bg-gradient-to-r from-blue-50 to-green-50 p-4 md:p-6 rounded-lg border border-blue-200">
          <label className="flex items-center text-base md:text-lg font-medium text-gray-800 mb-3 md:mb-4">
            <Calendar className="text-blue-600 mr-2" size={18} />
            Preferred Dates * (select multiple dates)
          </label>
          <DateCalendar 
            selectedDates={formData.preferredDates}
            onDateChange={handleDateChange}
          />
          {errors.preferredDates && (
            <p className="text-red-500 text-sm mt-2">{errors.preferredDates}</p>
          )}
        </div>

        {/* Time Preferences */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 md:p-6 rounded-lg border border-green-200">
          <label className="flex items-center text-base md:text-lg font-medium text-gray-800 mb-3 md:mb-4">
            <Clock className="text-green-600 mr-2" size={18} />
            Preferred Time Range
          </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <div>
            <label className="block text-sm md:text-base font-medium text-gray-700 mb-2">
              Start Time
            </label>
            <div className="relative">
              <select
                value={formData.startTime}
                onChange={(e) => {
                  const newStartTime = e.target.value;
                  const newStartHour = parseInt(newStartTime.split(':')[0]);
                  const currentEndHour = parseInt(formData.endTime.split(':')[0]);
                  
                  setFormData(prev => {
                    let newEndTime = prev.endTime;
                    // If current end time is not after start time, automatically adjust to next hour
                    if (currentEndHour <= newStartHour) {
                      const nextHour = Math.min(newStartHour + 1, 21);
                      newEndTime = `${nextHour.toString().padStart(2, '0')}:00`;
                    }
                    
                    return {
                      ...prev,
                      startTime: newStartTime,
                      endTime: newEndTime
                    };
                  });
                  
                  // Clear time range error
                  if (errors.timeRange) {
                    setErrors(prev => ({ ...prev, timeRange: undefined }));
                  }
                }}
                className="w-full border border-gray-300 rounded-md px-3 md:px-4 py-3 pr-10 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-green-500 bg-white min-h-[44px] appearance-none cursor-pointer"
              >
              {Array.from({ length: 16 }, (_, i) => {
                const hour = i + 6; // 6:00 to 21:00
                const timeStr = `${hour.toString().padStart(2, '0')}:00`;
                const displayText = hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`;
                return (
                  <option key={timeStr} value={timeStr}>
                    {displayText}
                  </option>
                );
              })}
              </select>
              {/* 自定义箭头图标 */}
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm md:text-base font-medium text-gray-700 mb-2">
              End Time
            </label>
            <div className="relative">
              <select
                value={formData.endTime}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, endTime: e.target.value }));
                  // Clear time range error
                  if (errors.timeRange) {
                    setErrors(prev => ({ ...prev, timeRange: undefined }));
                  }
                }}
                className="w-full border border-gray-300 rounded-md px-3 md:px-4 py-3 pr-10 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-h-[44px] appearance-none cursor-pointer"
              >
              {Array.from({ length: 16 }, (_, i) => {
                const hour = i + 6; // 6:00 to 21:00
                const timeStr = `${hour.toString().padStart(2, '0')}:00`;
                const startHour = parseInt(formData.startTime.split(':')[0]);
                
                // Only show options after start time
                if (hour <= startHour) {
                  return null;
                }
                
                const displayText = hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`;
                return (
                  <option key={timeStr} value={timeStr}>
                    {displayText}
                  </option>
                );
              }).filter(Boolean)}
              </select>
              {/* 自定义箭头图标 */}
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm md:text-base font-medium text-gray-700 mb-2">
              Party Size
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { value: 2, label: '2' },
                { value: 3, label: '3' },
                { value: 4, label: '4' },
                { value: 0, label: 'Any' }
              ].map((option) => (
                <label key={option.value} className="cursor-pointer">
                  <input
                    type="radio"
                    name="numberOfPlayers"
                    value={option.value}
                    checked={formData.numberOfPlayers === option.value}
                    onChange={(e) => setFormData(prev => ({ ...prev, numberOfPlayers: parseInt(e.target.value) }))}
                    className="sr-only"
                  />
                  <div className={`w-full px-3 py-3 text-center text-sm md:text-base font-medium rounded-md border transition-all duration-200 min-h-[44px] flex items-center justify-center ${
                    formData.numberOfPlayers === option.value
                      ? 'bg-blue-500 text-white border-blue-500 shadow-sm'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300 hover:bg-blue-50'
                  }`}>
                    {option.label}
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-3">
          Select your preferred golf time slots (6 AM - 9 PM) and party size. Choose "Any" to see all available time slots.
        </p>
        {errors.timeRange && (
          <p className="text-red-500 text-sm mt-2">{errors.timeRange}</p>
        )}
      </div>

      {/* Contact Information */}
      <div className="bg-gradient-to-r from-blue-50 to-green-50 p-4 md:p-6 rounded-lg border border-blue-200">
        <h3 className="flex items-center text-base md:text-lg font-medium text-gray-800 mb-3 md:mb-4">
          <Mail className="text-blue-600 mr-2" size={18} />
          Contact Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div>
            <label className="flex items-center text-sm md:text-base font-medium text-gray-700 mb-3">
              <Phone className="text-green-500 mr-2" size={16} />
              Phone Number <span className="text-xs md:text-sm text-gray-500 font-normal">(optional)</span>
            </label>
            <input
              type="tel"
              value={formData.contactPhone}
              onChange={(e) => setFormData(prev => ({ ...prev, contactPhone: e.target.value }))}
              className={`w-full border rounded-md px-3 md:px-4 py-3 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-green-500 bg-white min-h-[44px] ${
                errors.contactPhone ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="+1 (604) 123-4567"
            />
            {errors.contactPhone && (
              <p className="text-red-500 text-sm mt-2">{errors.contactPhone}</p>
            )}
          </div>
          <div>
            <label className="flex items-center text-sm md:text-base font-medium text-gray-700 mb-3">
              <Mail className="text-blue-500 mr-2" size={16} />
              Email Address *
            </label>
            <input
              type="email"
              value={formData.contactEmail}
              onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
              required
              className={`w-full border rounded-md px-3 md:px-4 py-3 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-h-[44px] ${
                errors.contactEmail ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="your@email.com"
            />
            {errors.contactEmail && (
              <p className="text-red-500 text-sm mt-2">{errors.contactEmail}</p>
            )}
          </div>
        </div>
      </div>

      {/* Notification Method - Email only currently supported */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 md:p-6 rounded-lg border border-green-200">
        <label className="flex items-center text-base md:text-lg font-medium text-gray-800 mb-3 md:mb-4">
          <Mail className="text-blue-600 mr-2" size={18} />
          Notification Method
        </label>
        <div className="bg-white p-3 md:p-4 rounded-md border border-gray-200">
          <div className="flex items-center">
            <Mail className="text-blue-500 mr-2 md:mr-3" size={18} />
            <span className="text-sm md:text-base text-gray-700 font-medium">Email Notifications</span>
            <span className="ml-2 text-xs md:text-sm text-green-600 bg-green-100 px-2 py-1 rounded-full">Enabled</span>
          </div>
          <p className="text-xs md:text-sm text-gray-600 mt-2">
            When we find matching golf course tee times, we'll send email notifications to your inbox.
          </p>
        </div>
      </div>

      {/* Submit Button */}
      <div className="pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full py-4 px-6 text-base md:text-lg rounded-lg font-medium transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-green-300 flex items-center justify-center space-x-2 min-h-[52px] ${
            isSubmitting
              ? 'bg-gray-400 text-white cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1'
          }`}
        >
          <Play className="text-lg md:text-xl" size={18} />
          <span>{isSubmitting ? 'Submitting...' : 'Join Waitlist'}</span>
        </button>
      </div>
    </form>

    {/* Success Message Modal */}
    <SuccessMessage
      isVisible={showSuccessMessage}
      onClose={() => setShowSuccessMessage(false)}
      title="Successfully Joined Waitlist!"
      message="Thank you for joining! We'll closely monitor your selected golf courses for available tee times."
    />

    {/* Error Message Modal */}
    <ErrorMessage
      isVisible={showErrorMessage}
      onClose={() => setShowErrorMessage(false)}
      title={errorDetails.title}
      message={errorDetails.message}
      errors={errorDetails.errors}
    />

    {/* Login Required Modal */}
    <LoginModal
      isOpen={showLoginModal}
      onClose={() => setShowLoginModal(false)}
      title="Login Required"
      message="Please sign in to join the waitlist and manage your golf tee time notifications."
    />
    </>
  );
};

export default MonitoringForm;